import { Handle, Position, type NodeProps, type Node } from '@xyflow/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Tag } from './ui'
import type { OntoNodeKind } from '../types'

interface OrionNodeData {
  kind: OntoNodeKind
  kindLabel?: string
  label: string
  detail?: string
  citation?: string
  expanded?: boolean
  onToggle?: () => void
  appearKey?: string
  appearDelay?: number
  attribution?: 'on' | 'off' | 'idle'
  [key: string]: unknown
}

export type OrionFlowNodeType = Node<OrionNodeData, 'orion'>

const KIND_STYLE: Record<
  OntoNodeKind,
  { color: string; bg: string; icon: string; ring: string }
> = {
  goal: {
    color: 'text-[color:var(--color-orion-amber)]',
    bg: 'bg-[color:var(--color-orion-amber)]/15 border-[color:var(--color-orion-amber)]/60',
    icon: '◆',
    ring: 'ring-[color:var(--color-orion-amber)]/45',
  },
  object: {
    color: 'text-[color:var(--color-orion-violet)]',
    bg: 'bg-[color:var(--color-orion-violet)]/13 border-[color:var(--color-orion-violet)]/50',
    icon: '○',
    ring: 'ring-[color:var(--color-orion-violet)]/45',
  },
  inference: {
    color: 'text-[color:var(--color-orion-cyan)]',
    bg: 'bg-[color:var(--color-orion-cyan)]/13 border-[color:var(--color-orion-cyan)]/50',
    icon: '△',
    ring: 'ring-[color:var(--color-orion-cyan)]/45',
  },
  constraint: {
    color: 'text-[color:var(--color-orion-warn)]',
    bg: 'bg-[color:var(--color-orion-warn)]/13 border-[color:var(--color-orion-warn)]/50',
    icon: '⬢',
    ring: 'ring-[color:var(--color-orion-warn)]/45',
  },
  candidate: {
    color: 'text-[color:var(--color-orion-info)]',
    bg: 'bg-[color:var(--color-orion-info)]/13 border-[color:var(--color-orion-info)]/50',
    icon: '◇',
    ring: 'ring-[color:var(--color-orion-info)]/45',
  },
  action: {
    color: 'text-[color:var(--color-orion-pass)]',
    bg: 'bg-[color:var(--color-orion-pass)]/15 border-[color:var(--color-orion-pass)]/60',
    icon: '▶',
    ring: 'ring-[color:var(--color-orion-pass)]/45',
  },
}

// Handle 不可见,但仍作为边的精确连接点(在节点 left/right 边线中心)
const HANDLE_STYLE: React.CSSProperties = {
  width: 1,
  height: 1,
  minWidth: 1,
  minHeight: 1,
  background: 'transparent',
  border: 'none',
  opacity: 0,
  pointerEvents: 'none',
}

export function OrionFlowNode({ data }: NodeProps<OrionFlowNodeType>) {
  const style = KIND_STYLE[data.kind]
  const kindLabel = data.kindLabel ?? data.kind
  const expanded = data.expanded ?? false
  const appearKey = data.appearKey ?? 'default'
  const appearDelay = data.appearDelay ?? 0
  const attribution = data.attribution ?? 'idle'

  const isDimmed = attribution === 'off'
  const isHighlight = attribution === 'on'

  return (
    <motion.div
      key={appearKey}
      initial={{ opacity: 0, scale: 0.92, y: 6 }}
      animate={{ opacity: isDimmed ? 0.25 : 1, scale: 1, y: 0 }}
      transition={{
        delay: appearDelay,
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1],
        opacity: { delay: appearDelay, duration: 0.3 },
      }}
      className={`nopan group rounded-[var(--radius-sm)] border backdrop-blur-sm ${style.bg} ${
        isHighlight
          ? `ring-2 ${style.ring} shadow-[0_0_24px_oklch(75%_0.16_65/0.4)]`
          : expanded
            ? `ring-1 ${style.ring}`
            : ''
      } min-w-[140px]`}
      style={{ cursor: 'pointer' }}
    >
      <Handle type="target" position={Position.Left} style={HANDLE_STYLE} />

      <div className="px-2.5 pt-1.5 pb-1 flex flex-col items-start gap-0.5 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <span className={`mono text-[10px] leading-none ${style.color}`} aria-hidden>
            {style.icon}
          </span>
          <span className={`mono text-[9px] uppercase tracking-[0.1em] ${style.color}`}>
            {kindLabel}
          </span>
        </div>
        <div className="mono text-[11.5px] text-[color:var(--color-orion-text)] leading-tight">
          {data.label}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (data.detail || data.citation) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="px-2.5 pb-2 pt-1 border-t border-[color:var(--color-orion-surface)]/40 space-y-1 max-w-[260px] whitespace-normal">
              {data.detail && (
                <p className="text-[10px] text-[color:var(--color-orion-haze)] leading-snug">
                  {data.detail}
                </p>
              )}
              {data.citation && (
                <Tag kind="standard" className="text-[9px]">
                  {data.citation}
                </Tag>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Handle type="source" position={Position.Right} style={HANDLE_STYLE} />
    </motion.div>
  )
}

